/**
 * Remortgage / Transfer of Equity Completion Checklist Configuration
 *
 * BM-H4191 (11 Oct 2026): Vanessa asked for remortgage and transfer of equity
 * workflows, for individuals and companies, "in the same way as the current
 * resi pipeline". Neither has an exchange, so this one checklist takes the
 * place of Pre-Exchange and Pre-Completion. Approving it makes the
 * Completions Book entry and the Post-Completion checklist, as approving the
 * Pre-Exchange and Pre-Completion checklists does for a purchase.
 *
 * Transaction types: "remortgage", "toe" (transfer of equity only) and
 * "remortgage_toe". Conditions: is_leasehold, is_company, not_company.
 * Item 8.1 is the completion date (it makes the completion deadline and the
 * Completions Book date); 1.2 is the mortgage offer expiry.
 */

const ALL = ["remortgage", "toe", "remortgage_toe"];
const LENDER = ["remortgage", "remortgage_toe"];
const TRANSFER = ["toe", "remortgage_toe"];

const REFINANCE_COMPLETION_CONFIG = {
  id: "refinance_completion",
  title: "Remortgage / Transfer of Equity Completion Checklist",
  subtitle: "Residential Conveyancing",
  newLabel: "Remortgage / Transfer of Equity",
  defaultTx: "remortgage",
  txOptions: [
    { value: "remortgage", label: "Remortgage" },
    { value: "toe", label: "Transfer of equity" },
    { value: "remortgage_toe", label: "Remortgage and transfer of equity" },
  ],
  pdfFooter: "This checklist is aligned with the UK Finance Mortgage Lenders' Handbook and the Law Society Conveyancing Quality Scheme Core Practice Management Standards (Risk Management, File & Case Management, Client Care).",
  sections: [
    {
      id: "instructions",
      title: "Instructions & Lender",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Mortgage offer summary"],
      items: [
        { id: "1.1", label: "Lender's instructions received and checked against the UK Finance Mortgage Lenders' Handbook (Part 1 and the lender's Part 2)", type: "checkbox", applies: LENDER },
        { id: "1.2", label: "Mortgage offer received and its special conditions noted (enter the offer expiry date)", type: "checkbox_date", applies: LENDER, deadline_type: "mortgage_offer" },
        { id: "1.3", label: "Existing lender(s) identified and redemption statement requested for the completion date", type: "checkbox", applies: LENDER },
        { id: "1.4", label: "Existing lender's consent to the transfer obtained, with its terms for the incoming and outgoing borrowers", type: "checkbox_notes", applies: ["toe"] },
      ],
    },
    {
      id: "title",
      title: "Title & Searches",
      items: [
        { id: "2.1", label: "Official copies and title plan obtained and reviewed", type: "checkbox", applies: ALL },
        { id: "2.2", label: "Registered owners match the borrowers / transferors", type: "checkbox", applies: ALL },
        { id: "2.3", label: "Restrictions and existing charges identified, and how each will be satisfied recorded", type: "checkbox_notes", applies: ALL },
        { id: "2.4", label: "Searches, or search indemnity insurance, as the lender requires", type: "checkbox_notes", applies: LENDER },
        { id: "2.5", label: "Bankruptcy search (K16) against every borrower and incoming owner", type: "checkbox_date", applies: ALL, condition: "not_company" },
        { id: "2.6", label: "OS1 priority search obtained", type: "checkbox_date", applies: ALL },
      ],
    },
    {
      id: "transfer",
      title: "Transfer of Equity",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Source of funds review"],
      items: [
        { id: "3.1", label: "Every party identified: who is joining, who is leaving and who is staying on the title", type: "checkbox_notes", applies: TRANSFER },
        { id: "3.2", label: "Conflict of interest considered; any party whose interests differ advised to take independent legal advice", type: "checkbox_notes", applies: TRANSFER },
        { id: "3.3", label: "Identity and AML checks complete for every incoming owner", type: "checkbox", applies: TRANSFER },
        { id: "3.4", label: "Consideration for the share agreed and recorded", type: "checkbox_amount", applies: TRANSFER },
        { id: "3.5", label: "Source of any money the client is paying for the share recorded", type: "checkbox_notes", applies: TRANSFER },
        { id: "3.6", label: "How the owners will hold confirmed (joint tenants or tenants in common); declaration of trust offered where shares are unequal", type: "checkbox_notes", applies: TRANSFER },
        { id: "3.7", label: "TR1 drafted, approved, and signed and witnessed by every party", type: "checkbox", applies: TRANSFER },
        { id: "3.8", label: "SDLT assessed on the chargeable consideration (including any share of mortgage debt taken on); return prepared, or why none is due recorded", type: "checkbox_amount", applies: TRANSFER },
      ],
    },
    {
      id: "mortgage",
      title: "Mortgage & Funds",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Remortgage completion checklist"],
      items: [
        { id: "4.1", label: "Mortgage deed signed and witnessed by every borrower", type: "checkbox", applies: LENDER },
        { id: "4.2", label: "Occupier's consent / waiver signed by every adult occupier (if the lender requires)", type: "checkbox", applies: LENDER },
        { id: "4.3", label: "Certificate of title / report on title sent to the lender", type: "checkbox_date", applies: LENDER },
        { id: "4.4", label: "Funds requested from the lender", type: "checkbox_date", applies: LENDER },
        { id: "4.5", label: "Mortgage advance received into client account", type: "checkbox_amount", applies: LENDER },
        { id: "4.6", label: "Redemption figure confirmed for the completion date", type: "checkbox_amount", applies: LENDER },
        { id: "4.7", label: "Any shortfall received from the client, or the surplus and where it goes agreed", type: "checkbox_amount", applies: LENDER },
        { id: "4.8", label: "Purpose of any additional borrowing recorded on the file", type: "checkbox_notes", applies: LENDER },
      ],
    },
    {
      id: "company",
      title: "Company Client",
      condition: "is_company",
      items: [
        { id: "5.1", label: "Companies House search: company active, officers confirmed, charges register checked, no insolvency", type: "checkbox_date", applies: ALL, condition: "is_company" },
        { id: "5.2", label: "Articles checked for any restriction on borrowing, charging or transferring property", type: "checkbox", applies: ALL, condition: "is_company" },
        { id: "5.3", label: "Board minute or resolution approving the transaction and authorising who signs", type: "checkbox", applies: ALL, condition: "is_company" },
        { id: "5.4", label: "Execution in line with Companies Act 2006 s44 (two directors, a director and the secretary, or one director with a witness)", type: "checkbox", applies: ALL, condition: "is_company" },
        { id: "5.5", label: "Directors' personal guarantees in place if the lender requires them, with independent advice recommended", type: "checkbox_notes", applies: LENDER, condition: "is_company" },
        { id: "5.6", label: "MR01 diarised: Companies House must receive it within 21 days beginning with the day after the charge is dated", type: "checkbox", applies: LENDER, condition: "is_company" },
        { id: "5.7", label: "Connected company market value rule (Finance Act 2003 s53), higher rates for companies and ATED considered", type: "checkbox_notes", applies: TRANSFER, condition: "is_company" },
      ],
    },
    {
      id: "leasehold",
      title: "Leasehold Specific",
      condition: "is_leasehold",
      items: [
        { id: "6.1", label: "Unexpired lease term meets the lender's requirements", type: "checkbox", applies: LENDER },
        { id: "6.2", label: "Landlord's consent or notice requirements for the charge / transfer identified and diarised", type: "checkbox_notes", applies: ALL },
      ],
    },
    {
      id: "undertakings_compliance",
      title: "Undertakings & Compliance",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["AML/ID summary"],
      items: [
        { id: "7.1", label: "Undertaking to redeem the existing charge(s) given to the new lender", type: "checkbox", applies: LENDER },
        { id: "7.2", label: "AML checks still within validity period", type: "checkbox", applies: ALL, autoCheck: "thirdfort" },
        { id: "cqs_file_note_updated_refinance", label: "Have you updated the file note to record today's completion planning and risk checks?", type: "radio", applies: ALL, required: true },
        { id: "cqs_risk_issue_flagged_refinance", label: "Any outstanding risk issues that should be flagged for CQS file review or the PI risk log?", type: "radio", applies: ALL, required: true },
        { id: "cqs_risk_issue_details_refinance", label: "If Yes, summarise risk and mitigation", type: "multiline", applies: ALL, conditionalOn: "cqs_risk_issue_flagged_refinance", conditionalValue: "yes" },
      ],
    },
    {
      id: "client_communication",
      title: "Client Communication",
      items: [
        { id: "8.1", label: "Completion date confirmed with the lender(s) and the client", type: "checkbox_date", applies: ALL, deadline_type: "completion_date" },
        { id: "8.2", label: "Client advised of the final financial position", type: "checkbox", applies: ALL },
        { id: "8.3", label: "Client reminded to keep buildings insurance in place with the lender's interest noted", type: "checkbox", applies: LENDER },
      ],
    },
    {
      id: "fee_earner_signoff",
      title: "Fee Earner Sign-off",
      items: [
        { id: "9.1", label: "Fee earner confirms all items complete and ready to complete", type: "signature", applies: ALL },
      ],
    },
  ],
};

export default REFINANCE_COMPLETION_CONFIG;
